// src/services/cofounder/tools/analysisTools.js
import { registerTool } from './ToolRegistry';
import { db } from '../../../firebase/config';
import { collection, query, where, getDocs, orderBy, limit, Timestamp } from 'firebase/firestore';

export function registerAnalysisTools() {
  registerTool({
    name: 'analyzeSalesTrends',
    description: 'Analyze sales trends comparing two time periods. Returns growth rate and changes.',
    category: 'read',
    requiresConfirmation: false,
    parameters: {
      type: 'object',
      properties: {
        period: {
          type: 'string',
          enum: ['weekly', 'monthly', 'quarterly'],
          description: 'Comparison period'
        }
      },
      required: ['period']
    },
    execute: async (args, context) => {
      const userId = context.userProfile?.id || context.currentUser?.uid;
      const businessId = context.activeBusiness?.id || context.userProfile?.businessId;
      if (!userId) return { error: 'No user ID' };

      const now = new Date();
      let currentStart, previousStart, previousEnd;

      if (args.period === 'weekly') {
        currentStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        previousEnd = new Date(currentStart.getTime());
        previousStart = new Date(previousEnd.getTime() - 7 * 24 * 60 * 60 * 1000);
      } else if (args.period === 'monthly') {
        currentStart = new Date(now.getFullYear(), now.getMonth(), 1);
        previousEnd = new Date(currentStart.getTime());
        previousStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      } else {
        const qMonth = Math.floor(now.getMonth() / 3) * 3;
        currentStart = new Date(now.getFullYear(), qMonth, 1);
        previousEnd = new Date(currentStart.getTime());
        previousStart = new Date(now.getFullYear(), qMonth - 3, 1);
      }

      const fetchSalesForPeriod = async (start, end) => {
        const constraints = [
          where('createdBy', '==', userId),
          where('date', '>=', Timestamp.fromDate(start)),
          where('date', '<=', Timestamp.fromDate(end)),
          orderBy('date', 'desc'),
          limit(200)
        ];
        if (businessId) {
          constraints.splice(1, 0, where('businessId', '==', businessId));
        }
        const q = query(
          collection(db, 'sales'),
          ...constraints
        );
        const snap = await getDocs(q);
        return snap.docs.map(d => d.data());
      };

      const [currentSales, previousSales] = await Promise.all([
        fetchSalesForPeriod(currentStart, now),
        fetchSalesForPeriod(previousStart, previousEnd)
      ]);

      const currentTotal = currentSales.reduce((sum, s) => sum + (s.totalAmount || 0), 0);
      const previousTotal = previousSales.reduce((sum, s) => sum + (s.totalAmount || 0), 0);
      const growthRate = previousTotal > 0
        ? ((currentTotal - previousTotal) / previousTotal * 100).toFixed(1)
        : 'N/A';

      return {
        period: args.period,
        current: { total: currentTotal, count: currentSales.length },
        previous: { total: previousTotal, count: previousSales.length },
        growthRate: `${growthRate}%`,
        trend: currentTotal > previousTotal ? 'up' : currentTotal < previousTotal ? 'down' : 'flat'
      };
    }
  });

  registerTool({
    name: 'identifyLowStockItems',
    description: 'Find inventory items that are below their minimum stock levels.',
    category: 'read',
    requiresConfirmation: false,
    parameters: {
      type: 'object',
      properties: {},
      required: []
    },
    execute: async (args, context) => {
      const userId = context.userProfile?.id || context.currentUser?.uid;
      const businessId = context.activeBusiness?.id || context.userProfile?.businessId;
      if (!userId) return { error: 'No user ID' };

      const constraints = [
        where('createdBy', '==', userId),
        limit(200)
      ];
      if (businessId) {
        constraints.splice(1, 0, where('businessId', '==', businessId));
      }
      const q = query(
        collection(db, 'stockItems'),
        ...constraints
      );
      const snap = await getDocs(q);
      const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      const lowStock = items.filter(item => {
        if (item.type === 'digital' || item.type === 'service') return false;
        let total = 0;
        Object.values(item.locations || {}).forEach(qty => { total += qty; });
        return total <= (item.minStockLevel || 0);
      }).map(item => {
        let total = 0;
        Object.values(item.locations || {}).forEach(qty => { total += qty; });
        return {
          name: item.name,
          currentStock: total,
          minStockLevel: item.minStockLevel || 0,
          category: item.category,
          sku: item.sku
        };
      });

      return { lowStockItems: lowStock, total: lowStock.length };
    }
  });

  registerTool({
    name: 'calculateCashFlow',
    description: 'Calculate cash flow summary for a given time period.',
    category: 'read',
    requiresConfirmation: false,
    parameters: {
      type: 'object',
      properties: {
        timePeriod: {
          type: 'string',
          enum: ['this_week', 'this_month', 'this_quarter', 'this_year'],
          description: 'Time period for cash flow calculation'
        }
      },
      required: ['timePeriod']
    },
    execute: async (args, context) => {
      const userId = context.userProfile?.id || context.currentUser?.uid;
      const businessId = context.activeBusiness?.id || context.userProfile?.businessId;
      if (!userId) return { error: 'No user ID' };

      const now = new Date();
      let startDate;

      switch (args.timePeriod) {
        case 'this_week':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'this_month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          break;
        case 'this_quarter':
          const qMonth = Math.floor(now.getMonth() / 3) * 3;
          startDate = new Date(now.getFullYear(), qMonth, 1);
          break;
        case 'this_year':
          startDate = new Date(now.getFullYear(), 0, 1);
          break;
        default:
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      }

      const constraints = [
        where('createdBy', '==', userId),
        where('date', '>=', Timestamp.fromDate(startDate)),
        where('date', '<=', Timestamp.fromDate(now)),
        orderBy('date', 'desc'),
        limit(500)
      ];
      if (businessId) {
        constraints.splice(1, 0, where('businessId', '==', businessId));
      }
      const q = query(
        collection(db, 'financialTransactions'),
        ...constraints
      );

      const snap = await getDocs(q);
      const transactions = snap.docs.map(d => d.data());

      const income = transactions.filter(t => t.amount > 0).reduce((s, t) => s + t.amount, 0);
      const expenses = transactions.filter(t => t.amount < 0).reduce((s, t) => s + Math.abs(t.amount), 0);

      return {
        period: args.timePeriod,
        income,
        expenses,
        netCashFlow: income - expenses,
        transactionCount: transactions.length
      };
    }
  });

  registerTool({
    name: 'getUpcomingDeadlines',
    description: 'Get upcoming task deadlines and overdue tasks within the next 7 days.',
    category: 'read',
    requiresConfirmation: false,
    parameters: {
      type: 'object',
      properties: {},
      required: []
    },
    execute: async (args, context) => {
      const userId = context.userProfile?.id || context.currentUser?.uid;
      const businessId = context.activeBusiness?.id || context.userProfile?.businessId;
      if (!userId) return { error: 'No user ID' };

      const now = new Date();
      const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

      const constraints = [
        where('createdBy', '==', userId),
        where('status', 'in', ['pending', 'in-progress']),
        limit(50)
      ];
      if (businessId) {
        constraints.splice(1, 0, where('businessId', '==', businessId));
      }
      const q = query(
        collection(db, 'tasks'),
        ...constraints
      );

      const snap = await getDocs(q);
      const tasks = snap.docs.map(d => ({ id: d.id, ...d.data() }));

      const overdue = [];
      const upcoming = [];

      tasks.forEach(task => {
        const dueDate = task.dueDate?.toDate ? task.dueDate.toDate() : task.dueDate ? new Date(task.dueDate) : null;
        if (!dueDate) return;

        if (dueDate < now) {
          overdue.push({ title: task.title, dueDate: dueDate.toISOString(), priority: task.priority });
        } else if (dueDate <= nextWeek) {
          upcoming.push({ title: task.title, dueDate: dueDate.toISOString(), priority: task.priority });
        }
      });

      return { overdue, upcoming, overdueCount: overdue.length, upcomingCount: upcoming.length };
    }
  });
}
